import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Minus, Plus } from "@phosphor-icons/react";

interface NumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  className?: string;
}

export function NumberStepper({
  value,
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  suffix,
  className,
}: NumberStepperProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external value changes when not editing
  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);

  const clamp = (n: number) => Math.min(max, Math.max(min, n));

  const commit = (raw: string) => {
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) {
      setDraft(String(value));
    } else {
      const clamped = clamp(parsed);
      onChange(clamped);
      setDraft(String(clamped));
    }
    setEditing(false);
  };

  const increment = () => onChange(clamp(value + step));
  const decrement = () => onChange(clamp(value - step));

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-xl border bg-background shadow-sm",
        className
      )}
    >
      <button
        type="button"
        onClick={decrement}
        disabled={value <= min}
        className="flex size-10 shrink-0 items-center justify-center rounded-l-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
      >
        <Minus className="size-4" />
      </button>

      <div className="flex items-center gap-1 border-x px-3">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={() => commit(draft)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit(draft);
              if (e.key === "Escape") { setDraft(String(value)); setEditing(false); }
            }}
            className="w-12 bg-transparent text-center text-sm font-semibold text-foreground outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setEditing(true);
              setDraft(String(value));
              setTimeout(() => inputRef.current?.select(), 0);
            }}
            className="w-12 text-center text-sm font-semibold text-foreground"
          >
            {value}
          </button>
        )}
        {suffix && (
          <span className="text-xs text-muted-foreground">{suffix}</span>
        )}
      </div>

      <button
        type="button"
        onClick={increment}
        disabled={value >= max}
        className="flex size-10 shrink-0 items-center justify-center rounded-r-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
      >
        <Plus className="size-4" />
      </button>
    </div>
  );
}
